import type { HsDecodedPointCloud } from '../data/decoded-geometry.interface';

/** Per-point RGB colors of a decoded point cloud, and how many points collide with the model. */
export interface HsScenePointCloudColors {
  /** Three bytes per point, in point order; `hsMakePointCloudGeometry` stores them as is. */
  colors: Uint8Array;
  collidedPoints: number;
}

const MISSING_COLOR: readonly number[] = [242, 122, 20];
const COLLISION_COLOR: readonly number[] = [235, 31, 20];

/**
 * Colors packed as 0xRRGGBB become RGB bytes; points without a color (-1 or 0xFFFFFFFF) are orange. With
 * `highlightCollisions`, points that collide with the model are red.
 */
export function hsScenePointCloudColors(
  pointCloud: HsDecodedPointCloud,
  highlightCollisions: boolean
): HsScenePointCloudColors {
  const pointCount: number = pointCloud.points.length / 3;
  const colors: Uint8Array = new Uint8Array(pointCount * 3);
  let collidedPoints: number = 0;
  for (let i: number = 0; i < pointCount; i++) {
    const hasCollision: boolean = (pointCloud.collisions[i] ?? 0) > 0;
    if (hasCollision) {
      collidedPoints++;
    }
    const offset: number = i * 3;
    if (highlightCollisions && hasCollision) {
      colors[offset] = COLLISION_COLOR[0];
      colors[offset + 1] = COLLISION_COLOR[1];
      colors[offset + 2] = COLLISION_COLOR[2];
      continue;
    }
    const color: number = pointCloud.colors[i] ?? -1;
    if (color === -1 || color === 0xffffffff) {
      colors[offset] = MISSING_COLOR[0];
      colors[offset + 1] = MISSING_COLOR[1];
      colors[offset + 2] = MISSING_COLOR[2];
    } else {
      colors[offset] = (color >> 16) & 255;
      colors[offset + 1] = (color >> 8) & 255;
      colors[offset + 2] = color & 255;
    }
  }
  return { colors, collidedPoints };
}
